/**
 * Pure division-registry invariant logic for the Division Registry feature.
 *
 * This module is intentionally free of I/O — every function operates over
 * in-memory collections of `Division` records so the same code can be exercised
 * by the `division-registry` Lambda (DynamoDB-backed) and by `jest` + `fast-check`
 * property tests. The DynamoDB shapes, resolvers, and the op-dispatch Lambda are
 * built in later tasks; this file holds only the rules that must hold regardless
 * of where the records are stored:
 *
 *   - key format/uniqueness within a competition year (Req 1.2, 1.9, 1.12)
 *   - label format (Req 1.3, 1.11)
 *   - `active` defaults true on create (Req 1.1)
 *   - rename leaves the `key` unchanged (Req 1.3)
 *   - deactivate retains the record + `key` (assignments live in a separate
 *     collection this module never touches) (Req 1.5)
 *   - `displayOrder` is a contiguous, gapless `1..N` permutation per year
 *     (Req 1.4)
 *   - selectability follows `active ∧ appliesTo*` (Req 1.7, 1.8)
 *
 * Every operation that mutates a collection is non-destructive: it returns a new
 * array and never mutates its inputs, which keeps the property tests' "before"
 * snapshots valid for comparison.
 *
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.10, 1.11, 1.12
 */

// ---------------------------------------------------------------------------
// Types

/**
 * A director-owned, runtime-configurable contestant category scoped to a single
 * competition year. Mirrors the `DivisionRecord` shape in the design's Data
 * Models section (the `${stage}-DivisionRegistry` table row).
 */
export interface Division {
  /** UUID — the table partition key. */
  id: string;
  /** The competition year that scopes this division (GSI partition key). */
  competitionYear: number;
  /** Stable machine key: 1..64 chars, `[a-z0-9-]`; unique per year. */
  key: string;
  /** Human-readable display name: 1..100 chars; editable via rename. */
  label: string;
  /**
   * Optional, nullable director-set title prefix for the division
   * (title-approval-tracking spec). When unset, callers fall back to `label`.
   * Additive — existing divisions simply have no prefix.
   */
  titlePrefix?: string;
  /** Contiguous `1..N` ordinal within the competition year. */
  displayOrder: number;
  /** Selectable as an application category when also `active`. */
  appliesToApplications: boolean;
  /** Selectable as a merit division when also `active`. */
  appliesToJudging: boolean;
  /** Defaults to true on create; toggled by activate/deactivate. */
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Fields a director supplies when creating a division. */
export interface CreateDivisionFields {
  competitionYear: number;
  key: string;
  label: string;
  /** Optional, nullable title prefix (title-approval-tracking spec). */
  titlePrefix?: string;
  appliesToApplications: boolean;
  appliesToJudging: boolean;
}

/** Validation/identity bounds, exported so tests and resolvers share one source. */
export const DIVISION_KEY_MIN_LENGTH = 1;
export const DIVISION_KEY_MAX_LENGTH = 64;
export const DIVISION_LABEL_MIN_LENGTH = 1;
export const DIVISION_LABEL_MAX_LENGTH = 100;

/** Lowercase letters, digits, and hyphens only (Req 1.2, 1.12). */
const DIVISION_KEY_PATTERN = /^[a-z0-9-]+$/;

// ---------------------------------------------------------------------------
// Errors

/** Thrown when a division key or label fails its format/length rules. */
export class DivisionValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DivisionValidationError';
  }
}

/** Thrown when a create would introduce a duplicate key within a year. */
export class DivisionKeyConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DivisionKeyConflictError';
  }
}

// ---------------------------------------------------------------------------
// Validation (Req 1.2, 1.3, 1.11, 1.12)

/**
 * Validate a division key: 1..64 characters limited to lowercase letters,
 * digits, and hyphens. Throws {@link DivisionValidationError} when invalid
 * (Req 1.2, 1.12).
 */
export function validateDivisionKey(key: unknown): asserts key is string {
  if (typeof key !== 'string') {
    throw new DivisionValidationError('Division key format is invalid: expected a string.');
  }
  if (
    key.length < DIVISION_KEY_MIN_LENGTH ||
    key.length > DIVISION_KEY_MAX_LENGTH ||
    !DIVISION_KEY_PATTERN.test(key)
  ) {
    throw new DivisionValidationError(
      `Division key format is invalid: must be ${DIVISION_KEY_MIN_LENGTH}-${DIVISION_KEY_MAX_LENGTH} ` +
        'characters limited to lowercase letters, digits, and hyphens.',
    );
  }
}

/**
 * Validate a division label: 1..100 characters. Throws
 * {@link DivisionValidationError} when empty or too long (Req 1.3, 1.11).
 */
export function validateDivisionLabel(label: unknown): asserts label is string {
  if (typeof label !== 'string') {
    throw new DivisionValidationError('Division label is invalid: expected a string.');
  }
  if (label.length < DIVISION_LABEL_MIN_LENGTH || label.length > DIVISION_LABEL_MAX_LENGTH) {
    throw new DivisionValidationError(
      `Division label is invalid: must be ${DIVISION_LABEL_MIN_LENGTH}-${DIVISION_LABEL_MAX_LENGTH} characters.`,
    );
  }
}

// ---------------------------------------------------------------------------
// Per-year scoping helpers (Req 1.10)

/** Divisions belonging to a single competition year. */
export function divisionsInYear(divisions: readonly Division[], competitionYear: number): Division[] {
  return divisions.filter((d) => d.competitionYear === competitionYear);
}

/**
 * Whether a key is already in use within a competition year (Req 1.9). Used by
 * create to enforce per-year key uniqueness.
 */
export function keyExistsInYear(
  divisions: readonly Division[],
  competitionYear: number,
  key: string,
): boolean {
  return divisions.some((d) => d.competitionYear === competitionYear && d.key === key);
}

/**
 * The `displayOrder` a newly created division receives: appended after the
 * existing divisions in the year so the year stays contiguous `1..N` (Req 1.4).
 */
export function assignDisplayOrderOnCreate(
  divisions: readonly Division[],
  competitionYear: number,
): number {
  return divisionsInYear(divisions, competitionYear).length + 1;
}

// ---------------------------------------------------------------------------
// Reorder (Req 1.4)

/**
 * Map an ordered list of keys to contiguous `displayOrder` values
 * `displayOrder = index + 1`, yielding a gapless, duplicate-free `1..N`
 * sequence. Throws if `orderedKeys` itself contains duplicates, since that could
 * not produce a valid permutation.
 */
export function reorderToContiguous(orderedKeys: readonly string[]): Map<string, number> {
  const seen = new Set<string>();
  const result = new Map<string, number>();
  orderedKeys.forEach((key, index) => {
    if (seen.has(key)) {
      throw new DivisionValidationError(`Cannot reorder: duplicate division key "${key}".`);
    }
    seen.add(key);
    result.set(key, index + 1);
  });
  return result;
}

// ---------------------------------------------------------------------------
// Pure operations over a division collection
//
// Each returns a new array and never mutates its inputs.

/**
 * Create a division within a year. Validates the key and label, enforces
 * per-year key uniqueness, assigns the next contiguous `displayOrder`, and sets
 * `active` true by default (Req 1.1, 1.2, 1.9, 1.11, 1.12).
 *
 * @param now ISO timestamp used for `createdAt`/`updatedAt` (injected so tests
 *            stay deterministic).
 */
export function createDivision(
  divisions: readonly Division[],
  fields: CreateDivisionFields,
  id: string,
  now: string,
): Division[] {
  validateDivisionKey(fields.key);
  validateDivisionLabel(fields.label);

  if (keyExistsInYear(divisions, fields.competitionYear, fields.key)) {
    throw new DivisionKeyConflictError(
      `Division key "${fields.key}" is already in use within competition year ${fields.competitionYear}.`,
    );
  }

  // `titlePrefix` is optional and nullable: trim it, and treat an empty/blank
  // string as "no prefix" (left undefined) so the record never persists an
  // empty prefix that would shadow the `label` fallback.
  const trimmedPrefix = fields.titlePrefix?.trim();

  const created: Division = {
    id,
    competitionYear: fields.competitionYear,
    key: fields.key,
    label: fields.label,
    ...(trimmedPrefix ? { titlePrefix: trimmedPrefix } : {}),
    displayOrder: assignDisplayOrderOnCreate(divisions, fields.competitionYear),
    appliesToApplications: fields.appliesToApplications,
    appliesToJudging: fields.appliesToJudging,
    active: true,
    createdAt: now,
    updatedAt: now,
  };

  return [...divisions, created];
}

/**
 * Rename a division: updates the label (validated) and leaves the `key`
 * unchanged (Req 1.3). Returns the collection unchanged-by-reference except for
 * the renamed record.
 *
 * `titlePrefix` (title-approval-tracking spec) is an optional, additive write
 * path threaded through the same mutation:
 *   - omitted (`undefined`) ⇒ leave the existing `titlePrefix` untouched;
 *   - a non-empty string ⇒ set the trimmed prefix;
 *   - an empty/blank string ⇒ clear the prefix (back to `undefined`, so callers
 *     fall back to `label`).
 */
export function renameDivision(
  divisions: readonly Division[],
  competitionYear: number,
  key: string,
  label: string,
  now: string,
  titlePrefix?: string,
): Division[] {
  validateDivisionLabel(label);
  const prefixProvided = titlePrefix !== undefined;
  const trimmedPrefix = titlePrefix?.trim();
  return divisions.map((d) => {
    if (d.competitionYear !== competitionYear || d.key !== key) {
      return d;
    }
    const next: Division = { ...d, label, updatedAt: now };
    if (prefixProvided) {
      if (trimmedPrefix) {
        next.titlePrefix = trimmedPrefix;
      } else {
        delete next.titlePrefix;
      }
    }
    return next;
  });
}

/**
 * Set a division's `active` flag. Deactivation (`active = false`) retains the
 * record and its `key`; contestant-number assignments live in a separate
 * collection that this module never touches, so they are preserved by
 * construction (Req 1.5, 1.6).
 */
export function setDivisionActive(
  divisions: readonly Division[],
  competitionYear: number,
  key: string,
  active: boolean,
  now: string,
): Division[] {
  return divisions.map((d) =>
    d.competitionYear === competitionYear && d.key === key
      ? { ...d, active, updatedAt: now }
      : d,
  );
}

/**
 * Reorder a year's divisions to the given key order, persisting contiguous
 * `1..N` `displayOrder` values (Req 1.4). `orderedKeys` must list exactly the
 * keys present in the year; any mismatch throws so a reorder can never leave the
 * year with gaps or duplicates.
 */
export function reorderDivisions(
  divisions: readonly Division[],
  competitionYear: number,
  orderedKeys: readonly string[],
  now: string,
): Division[] {
  const yearKeys = new Set(divisionsInYear(divisions, competitionYear).map((d) => d.key));
  const requested = new Set(orderedKeys);

  if (requested.size !== orderedKeys.length) {
    throw new DivisionValidationError('Cannot reorder: orderedKeys contains duplicate keys.');
  }
  if (yearKeys.size !== requested.size || [...yearKeys].some((k) => !requested.has(k))) {
    throw new DivisionValidationError(
      `Cannot reorder: orderedKeys must list exactly the divisions in competition year ${competitionYear}.`,
    );
  }

  const orderByKey = reorderToContiguous(orderedKeys);
  return divisions.map((d) =>
    d.competitionYear === competitionYear
      ? { ...d, displayOrder: orderByKey.get(d.key) as number, updatedAt: now }
      : d,
  );
}

// ---------------------------------------------------------------------------
// Selectability (Req 1.7, 1.8)

/** A division is selectable as an application category iff active ∧ appliesToApplications. */
export function isApplicationSelectable(division: Division): boolean {
  return division.active && division.appliesToApplications;
}

/** A division is selectable as a merit division iff active ∧ appliesToJudging. */
export function isMeritSelectable(division: Division): boolean {
  return division.active && division.appliesToJudging;
}
